import React, { useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { palette } from 'styled-theme'
import { Flex, Box } from '@rebass/grid'
import { BiReset } from 'react-icons/bi'
import { FaReply } from 'react-icons/fa'
import { IoClose } from 'react-icons/io5'
import { LuBotMessageSquare } from 'react-icons/lu'
import Text from '../../atoms/Text'
import Image from '../../atoms/Image'
import Select from '../../atoms/Select'
import Button from '../../atoms/Button'
import Tooltip from '../../atoms/Tooltip'
import Message from '../../molecules/Message'
import MessageSender from '../../molecules/MessageSender'
import MessageQuotaBox from '../../molecules/MessageQuotaBox'

const MESSAGE_SCROLL_BOTTOM_THRESHOLD = window.innerHeight / 2

const ChatHistoryContainer = styled(Flex)`
  flex-direction: column;
  flex: 1 1 auto;
  overflow: auto;
`

const MessageWrapper = styled(Flex)`
  /* overflow-anchor: none; */ // Removed for using the javaScript scrollIntoView logic
  margin-bottom: 0.25rem;
  justify-content: center;
  align-items: center;
`

const AnchorDiv = styled.div`
  min-height: 1px;
  /* overflow-anchor: auto; */ // Removed for using the javaScript scrollIntoView logic
`

const SystemMessage = styled(Flex)`
  background: ${palette('primary', 3)};
  padding: 0.25rem 1rem;
  border-radius: 0.5rem;
  box-shadow: 0 0.0625rem 0.125rem ${palette('grayscale', 2)};
`

const ReplyingMessageContainer = styled(Flex)`
  align-items: center;
  padding: 0.5rem;
  padding-right: 0;
  margin-bottom: 0.5rem;
  background-color: ${palette('grayscale', 4)}BF;
  border-radius: 0.5rem;
`

const ReplyingMessage = styled(Flex)`
  flex-direction: column;
  padding: 0.8rem 1rem;
  background-color: ${palette('primary', 0)}33;
  border-left: 0.4rem solid ${palette('primary', 3)}CC;
  border-radius: 0.5rem;
  overflow: hidden;
`

const Chatroom = ({
  mode,
  userId,
  username,
  messages,
  onChangeMode,
  messageQuota,
  messageRemaining,
  customSection,
  onMessageSend,
  onGetSuggestedReplies,
  onReset,
  onTopUp,
  readOnly,
  inputDisabled,
  resetRecommended,
  resetTooltipOpen,
  onCloseResetTooltip,
  resetTooltipContent,
  headerHidden,
  resettable,
  replyable,
  replySuggestable,
  imageSendable,
  chatroomRef,
  style,
  ...props
}) => {
  const [initialised, setInitialised] = useState(false)
  const [replyingMessageId, setReplyingMessageId] = useState(null)
  const [footerHeight, setFooterHeight] = useState(0)
  const [appendedImages, setAppendedImages] = useState([])
  const [suggestedReplies, setSuggestedReplies] = useState([])
  const [presetMessage, setPresetMessage] = useState('')
  const [imageLoadedCount, setImageLoadedCount] = useState(0)
  const appendedImagesRef = useRef(appendedImages)
  const messagesContainerRef = useRef()
  const scrollBottom = useRef(0)
  const bottomAnchorRef = useRef()

  const sendMessageWithReplyAndImages = useCallback((msgStr) => {
    const appendedImageFiles = (appendedImages || []).map(({ file }) => file)
    return Promise.resolve(
      onMessageSend(msgStr, replyingMessageId, appendedImageFiles),
    )
      .then(() => {
        setReplyingMessageId(null)
        setAppendedImages([])
      })
  }, [replyingMessageId, appendedImages, onMessageSend])

  const getSuggestedReplies = useCallback(() => {
    return Promise.resolve(onGetSuggestedReplies())
      .then((replies) => {
        setSuggestedReplies(replies)
        bottomAnchorRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        })
      })
  }, [onGetSuggestedReplies])

  const onPickImages = useCallback((files) => {
    const newAppendedImages = (files || []).map((file) => ({
      file,
      url: URL.createObjectURL(file),
    }))
    setAppendedImages((prev) => [...prev, ...newAppendedImages])
  }, [])

  const onRemoveImage = useCallback((rIdx) => {
    setAppendedImages((prev) => {
      URL.revokeObjectURL(prev[rIdx]?.url)
      return prev.filter((_, i) => i !== rIdx)
    })
  }, [])

  const ReplyButton = useCallback(({ messageId, isMyMessage }) => (
    <Button
      variant="text"
      padding="0.625rem"
      onClick={() => setReplyingMessageId(messageId)}
    >
      <FaReply
        size={16}
        style={isMyMessage ? { transform: 'scale(-1, 1)' } : {}}
      />
    </Button>
  ), [])

  const replyingMessage = useMemo(
    () => messages.find((m) => m.message_id === replyingMessageId),
    [messages, replyingMessageId],
  )

  useImperativeHandle(chatroomRef, () => ({
    appendedImages,
    onPickImages,
  }), [appendedImages, onPickImages])

  useEffect(() => {
    appendedImagesRef.current = appendedImages
  }, [appendedImages])

  useEffect(() => {
    return () => {
      (appendedImagesRef.current || []).forEach((image) => {
        URL.revokeObjectURL(image.url)
      })
    }
  }, [])

  useEffect(() => {
    if (messagesContainerRef.current) {
      const messagesContainer = messagesContainerRef.current
      const scrollHandler = () => {
        const newScrollBottom = messagesContainer.scrollHeight - messagesContainer.scrollTop - messagesContainer.clientHeight
        scrollBottom.current = newScrollBottom
      }
      messagesContainer.addEventListener('scroll', scrollHandler)
      return () => {
        messagesContainer.removeEventListener('scroll', scrollHandler)
      }
    }
  }, [])

  useEffect(() => {
    if (bottomAnchorRef.current && messages.length > 0) {
      if (!initialised) {
        bottomAnchorRef.current.scrollIntoView()
        const messageImageCount = messages.reduce((count, message) => {
          const numberOfImages = message.message_images?.length || 0
          return count + numberOfImages
        }, 0)
        if (imageLoadedCount >= messageImageCount) {
          setInitialised(true)
        }
      } else if (scrollBottom.current <= MESSAGE_SCROLL_BOTTOM_THRESHOLD) {
        bottomAnchorRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        })
      }
    }
  }, [initialised, messages, footerHeight, imageLoadedCount, customSection])

  return (
    <Flex flexDirection="column" style={{ position: 'relative', ...style }} {...props}>
      {!headerHidden && (
        <Flex justifyContent="space-between" alignItems="center" mb="0.5rem" ml="0.5rem">
          <Flex alignItems="center">
            <Text variant="body1" style={{ marginRight: 4 }}>模式: </Text>
            <Select
              size="small"
              value={mode}
              onChange={onChangeMode}
              options={[
                { label: 'Match人 👩‍❤️‍👨', value: 'users' },
                { label: '搵野搞 🎉', value: 'events' },
              ]}
            />
          </Flex>
          <MessageQuotaBox
            quota={messageQuota}
            remaining={messageRemaining}
            onClickTopUp={onTopUp}
            username={username}
          />
        </Flex>
      )}
      <ChatHistoryContainer ref={messagesContainerRef} pb={footerHeight}>
        {messages.map((message, i) => {
          const isMyMessage = (message.sender?.user_id === userId)
          const isSystemMessage = (message.sender?.user_id === 0)
          return (
            <MessageWrapper key={`message-${i}`} mt="0.4rem">
              {isSystemMessage ? (
                <SystemMessage>
                  <Text align="center">{message.content}</Text>
                </SystemMessage>
              ) : (
                <>
                  {isMyMessage && <Box flex="1 1 0" />}
                  {isMyMessage && replyable && (
                    <ReplyButton messageId={message.message_id} isMyMessage />
                  )}
                  <Message
                    message={message}
                    align={isMyMessage ? 'right' : 'left'}
                    onImageLoad={() => setImageLoadedCount((prev) => prev + 1)}
                  />
                  {!isMyMessage && replyable && (
                    <ReplyButton messageId={message.message_id} />
                  )}
                  {!isMyMessage && <Box flex="1 1 0" />}
                </>
              )}
            </MessageWrapper>
          )
        })}
        {customSection}
        {suggestedReplies?.length > 0 && (
          <Flex
            flexWrap="wrap"
            alignItems="center"
            justifyContent="flex-end"
            mt="auto"
            pt="0.5rem"
            style={{ gap: '0.5rem' }}
          >
            {suggestedReplies.map((reply, rIdx) => (
              <Button
                key={`suggested-reply-${rIdx}`}
                variant="outline"
                onClick={() => setPresetMessage(reply)}
              >
                {reply}
              </Button>
            ))}
          </Flex>
        )}
        <AnchorDiv ref={bottomAnchorRef} />
      </ChatHistoryContainer>
      <Flex
        mt="0.5rem"
        flexDirection="column"
        style={{ position: 'absolute', bottom: 0, left: 0, right: 0 }}
        ref={(refA) => {
          if (refA) setFooterHeight(refA.clientHeight + 8)
        }}
      >
        {replyingMessage && (
          <ReplyingMessageContainer>
            <ReplyingMessage flex={1}>
              <Flex mb="0.2rem">
                <Text bold variant="body1" palette="grayscale" paletteIndex={1}>
                  {replyingMessage.sender?.display_name}
                </Text>
              </Flex>
              {replyingMessage.message_images?.length > 0 && (
                <Flex mb="0.2rem" style={{ gap: '0.4rem' }}>
                  {replyingMessage.message_images.map((img, miIdx) => (
                    <Image
                      key={miIdx}
                      src={img.image_url}
                      height="60px"
                      borderRadius="0.5rem"
                      style={{ objectFit: 'contain' }}
                    />
                  ))}
                </Flex>
              )}
              <Text
                variant="body1"
                palette="grayscale"
                paletteIndex={1}
                style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
              >
                {replyingMessage.content}
              </Text>
            </ReplyingMessage>
            <Flex p="0.1rem" marginLeft="0.25rem" padding="0.75rem">
              <Button
                padding={0}
                palette="grayscale"
                style={{ borderRadius: '50%', width: '1.5rem', height: '1.5rem' }}
                disabled={readOnly}
                onClick={() => setReplyingMessageId(null)}
              >
                <IoClose size={18} />
              </Button>
            </Flex>
          </ReplyingMessageContainer>
        )}
        {appendedImages.length > 0 && (
          <ReplyingMessageContainer>
            <Flex flexWrap="wrap" alignItems="center" mt="0.5rem" style={{ gap: '0.5rem' }}>
              {appendedImages.map((img, iIdx) => (
                <Flex
                  key={`append-image-${iIdx}`}
                  alignItems="center"
                  justifyContent="center"
                  style={{ position: 'relative' }}
                >
                  <Image
                    src={img.url}
                    width={100}
                    height={100}
                    borderRadius="0.5rem"
                    style={{ objectFit: 'contain' }}
                  />
                  <Button
                    padding={0}
                    palette="error"
                    onClick={() => onRemoveImage(iIdx)}
                    style={{ position: 'absolute', top: 2, right: 2, borderRadius: '50%' }}
                  >
                    <IoClose size={20} />
                  </Button>
                </Flex>
              ))}
            </Flex>
          </ReplyingMessageContainer>
        )}
        <Flex alignItems="flex-end">
          {resettable && (
            <Flex mr="0.25rem">
              <Button
                padding="0.625rem"
                palette="grayscale"
                onClick={onReset}
                disabled={readOnly}
                id="chat_reset_btn"
                {...(resetRecommended && !readOnly ? {
                  className: 'blink',
                } : {})}
              >
                <BiReset size={24} />
              </Button>
            </Flex>
          )}
          {replySuggestable && (
            <Flex mr="0.25rem">
              <Button
                padding="0.4375rem 0.1rem"
                variant="text"
                onClick={getSuggestedReplies}
                disabled={readOnly}
              >
                <LuBotMessageSquare size={30} />
              </Button>
            </Flex>
          )}
          <MessageSender
            flex={1}
            hasImagePicker={imageSendable}
            onSend={sendMessageWithReplyAndImages}
            onPickImage={onPickImages}
            presetMessage={presetMessage}
            imageCount={appendedImages.length}
            disabled={readOnly || inputDisabled}
          />
        </Flex>
      </Flex>
      {resetRecommended && (
        <Tooltip
          place="top-start"
          anchorId="chat_reset_btn"
          open={resetTooltipOpen}
        >
          {resetTooltipContent}
          <Flex justifyContent="flex-end" mt={1}>
            <Button
              palette="grayscale"
              padding="0.25rem 0.5rem"
              onClick={onCloseResetTooltip}
            >
              <Text palette="white" variant="body2">知道</Text>
            </Button>
          </Flex>
        </Tooltip>
      )}
    </Flex>
  )
}

Chatroom.propTypes = {
  messages: PropTypes.array,
  userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  username: PropTypes.string,
  mode: PropTypes.oneOf(['users', 'events']),
  onChangeMode: PropTypes.func,
  messageQuota: PropTypes.number,
  messageRemaining: PropTypes.number,
  customSection: PropTypes.node,
  onMessageSend: PropTypes.func,
  onGetSuggestedReplies: PropTypes.func,
  onReset: PropTypes.func,
  onTopUp: PropTypes.func,
  readOnly: PropTypes.bool,
  inputDisabled: PropTypes.bool,
  resetRecommended: PropTypes.bool,
  resetTooltipOpen: PropTypes.bool,
  onCloseResetTooltip: PropTypes.func,
  resetTooltipContent: PropTypes.node,
  headerHidden: PropTypes.bool,
  resettable: PropTypes.bool,
  replyable: PropTypes.bool,
  replySuggestable: PropTypes.bool,
  imageSendable: PropTypes.bool,
  chatroomRef: PropTypes.shape({ current: PropTypes.any }),
  style: PropTypes.object,
}

Chatroom.defaultProps = {
  messages: [],
  userId: null,
  mode: 'users',
  onChangeMode: () => {},
  messageQuota: 0,
  messageRemaining: 0,
  customSection: null,
  onMessageSend: () => {},
  onGetSuggestedReplies: () => {},
  onReset: () => {},
  onTopUp: () => {},
  readOnly: false,
  inputDisabled: false,
  resetRecommended: false,
  resetTooltipOpen: false,
  onCloseResetTooltip: () => {},
  resetTooltipContent: '',
  headerHidden: false,
  resettable: false,
  replyable: false,
  replySuggestable: false,
  imageSendable: false,
  chatroomRef: null,
  style: {},
}

export default Chatroom
